import { createBrowserRouter, useNavigate, useParams } from 'react-router'
import { RootLayout } from './RootLayout'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { ThreadPage } from '@/features/thread/ThreadPage'
import { AudienceExpansionPage } from '@/features/audience/AudienceExpansionPage'
import { ThreadsSummaryPage } from '@/features/thread/ThreadsSummaryPage'
import { AudiencesPage } from '@/features/audience/AudiencesPage'
import { DistributionsPage } from '@/features/distribution/DistributionsPage'
import { DestinationsPage } from '@/features/destination/DestinationsPage'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { DesignPage } from './DesignPage' // design-system style guide (type, color, buttons)
import { withAuth } from './ProtectedRoute'
import { Auth0Provider, type AppState } from '@auth0/auth0-react'
import { useConfig } from './config'
import type { FC } from 'react'
import { genericMemo } from '@/components/genericMemo'
import { useGetSessionIdFromThreadId } from '@/features/thread/queries'

// Create protected versions of the components
const ProtectedDashboardPage = withAuth(DashboardPage)

const AudienceThreadPage: FC = genericMemo(() => {
    const { threadId } = useParams<{ threadId: string }>()
    if (!threadId) {
        return <div>No thread ID provided.</div>
    }
    return <ThreadPage threadId={threadId} />
})
const ProtectedAudienceThreadPage = withAuth(AudienceThreadPage)

const ProtectedThreadsSummaryPage = withAuth(ThreadsSummaryPage)

const ProtectedAudiencesPage = withAuth(AudiencesPage)
const ProtectedDistributionsPage = withAuth(DistributionsPage)
const ProtectedDestinationsPage = withAuth(DestinationsPage)

const ProtectedSessionIdFromThreadIdPage = withAuth(() => {
    const { threadId } = useParams<{
        threadId: string
    }>()
    if (!threadId) {
        return <div>No thread ID provided.</div>
    }
    const {
        data: sessionId,
        isLoading,
        isError,
        error,
    } = useGetSessionIdFromThreadId(threadId)
    if (isLoading) {
        return <div>Loading session ID...</div>
    }
    if (isError) {
        return <div>Error loading session ID: {error?.message}</div>
    }
    return (
        <pre>
            Session details for thread ID: {threadId}
            <br />
            Session ID: {JSON.stringify(sessionId)}
        </pre>
    )
})

const Auth0ProviderWithRedirectCallback = () => {
    const navigate = useNavigate()
    const config = useConfig()
    const onRedirectCallback = (appState: AppState | undefined) => {
        console.log('AppState:', appState)
        console.log('Window Location:', window.location.pathname)
        void navigate(
            (appState && appState.returnTo) || window.location.pathname
        )
    }
    return (
        <Auth0Provider
            domain={config.auth0Domain}
            clientId={config.auth0ClientId}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: config.auth0Audience,
                scope: config.auth0Scope || 'openid profile email',
                organization: config.subdomain,
            }}
            onRedirectCallback={onRedirectCallback}
        >
            <RootLayout />
        </Auth0Provider>
    )
}

export const router = createBrowserRouter([
    {
        path: '/',
        Component: Auth0ProviderWithRedirectCallback,
        ErrorBoundary: ErrorBoundary,
        children: [
            {
                index: true,
                Component: ProtectedDashboardPage,
            },
            {
                path: 'thread',
                ErrorBoundary: ErrorBoundary,
                children: [
                    {
                        index: true,
                        Component: ProtectedThreadsSummaryPage,
                    },
                    {
                        path: ':threadId',
                        Component: ProtectedAudienceThreadPage,
                    },
                    {
                        path: ':threadId/session',
                        Component: ProtectedSessionIdFromThreadIdPage,
                    },
                    {
                        path: ':threadId/expansion',
                        Component: withAuth(AudienceExpansionPage),
                    },
                ],
            },
            {
                path: 'audiences',
                Component: ProtectedAudiencesPage,
            },
            {
                path: 'distributions',
                Component: ProtectedDistributionsPage,
            },
            {
                path: 'destinations',
                Component: ProtectedDestinationsPage,
            },
            {
                // Design-system style guide: typography, buttons, brand mark,
                // semantic + categorical/chart color across real components, in
                // both schemes. Protected like every other route so Auth0
                // establishes the session and the header controls render.
                path: 'design',
                Component: withAuth(DesignPage),
            },
        ],
    },
])
